import { useState, useEffect } from 'react';
import { Box, Flex } from 'reflexbox';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';

const Table = styled.div`
	display: flex;
	flex-grow: 1;
	flex-shrink: 1;
	flex-direction: column;
	height: 100%;
`;

const TableContents = styled.div`
	position: relative;
	flex-grow: 1;
	flex-shrink: 1;
`;

const TableRow = styled(Flex)<{ header?: boolean; clickable?: boolean }>`
	flex-direction: row;
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlueLight};
	align-items: stretch;
	${(props) =>
		props.clickable &&
		`
		cursor: pointer;

		&:hover {
			background: ${props.theme.colors.lightBlueLight};
		}
	`}
`;

const TableCell = styled.div<{
	small?: boolean;
	bold?: boolean;
	sortable?: boolean;
	width?: number;
}>`
	padding: 10px;
	color: ${(props) => props.theme.colors.slateBlue};
	width: ${(props) => props.width ?? (props.small ? 50 : 150)}px;
	flex-shrink: 0;
	flex-grow: 0;
	overflow: hidden;
	white-space: nowrap;
	${(props) =>
		props.small ? null : `border-right: 1px solid ${props.theme.colors.lightBlueLight}`};
	display: flex;
	align-items: center;
	flex-flow: row;
	${(props) => props.bold && `font-weight: bold;`}
`;

export default function FailedSsoAttemptsListContainer() {
	const { t } = useTranslation();

	const { data, loading } = graphql.useFailedSsoAttemptsQuery({
		variables: {
			last: 100,
		},
	});

	const [attempts, setAttempts] = useState<graphql.FailedSsoAttempt[]>([]);

	useEffect(() => {
		if (data?.failedSsoAttempts) {
			setAttempts(data.failedSsoAttempts.edges.map(({ node }) => node).reverse());
		}
	}, [data?.failedSsoAttempts]);

	return (
		<Box>
			<Box mb={3}>
				<SectionTitle>{t('common.settings.failedSsoAttempts.title')}</SectionTitle>
			</Box>
			<Box mb={3}>
				<ParagraphSmall>{t('common.settings.failedSsoAttempts.description')}</ParagraphSmall>
			</Box>

			{loading && <Spinner isLoading />}

			{!loading && attempts && (
				<Table>
					<TableRow header>
						<TableCell>{t('common.settings.failedSsoAttempts.columns.occurredAt')}</TableCell>
						<TableCell width={400}>{t('common.settings.failedSsoAttempts.columns.externalID')}</TableCell>
					</TableRow>
					<TableContents>
						{attempts.map((row) => (
							<TableRow key={row.id}>
								<TableCell>{moment(row.occurredAt).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
								<TableCell width={400}>{row.externalID}</TableCell>
							</TableRow>
						))}
					</TableContents>
				</Table>
			)}
		</Box>
	);
}
